import { useEffect, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "./cn";

type Width = "md" | "lg" | "xl" | "2xl";

/**
 * Panel widths. Even the widest stops short of the viewport so the backdrop keeps signalling
 * "this is a layer over the page" rather than reading as a navigation.
 */
const WIDTHS: Record<Width, string> = {
  md: "max-w-md",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  "2xl": "max-w-6xl",
};

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: Width;
}

/**
 * A right-hand slide-over panel — the single editing surface in this application.
 *
 * <p>Everything that would otherwise be a modal dialog is one of these. A drawer keeps the page
 * behind it visible, which matters when the form's content depends on what the reader was just
 * looking at: adding a team member while the current membership list stays on screen is a
 * different experience from having it replaced by a box in the middle of the page.
 *
 * <p>The backdrop deliberately does <strong>not</strong> close the panel. A stray click outside a
 * half-filled form is the most common way to lose work, and there is nothing to be gained by
 * making that easy — closing is via the X or an explicit Cancel.
 */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = "md",
}: DrawerProps) {
  // Escape closes. Attached only while open, so a closed drawer does not intercept the key from
  // whatever else is on the page.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // The page behind must not scroll while a panel is over it, or the reader loses their place.
  useEffect(() => {
    if (!open) {
      return;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-ink-900/40 backdrop-blur-sm" />
      <div
        className={cn(
          "drawer-in relative flex h-full w-full flex-col bg-surface shadow-2xl",
          WIDTHS[width],
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-ink-100 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-ink-900">{title}</h2>
            {subtitle && <p className="mt-0.5 text-sm text-ink-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>

        {footer && <div className="border-t border-ink-100 px-5 py-4">{footer}</div>}
      </div>
    </div>
  );
}
